import { useEffect } from "react";
import type { Ticket } from "@omnabase/shared";
import { listUrl, useCustomer, useFetch, type TicketPage } from "../../lib/inbox";
import { useFacts } from "../../lib/integrations";
import type { PresenceRow } from "../../lib/presence";

/** The ticket pane's side panel (polish round two): the customer's profile, what the company's own system knows about them (M5 round five) and
 *  their other tickets. `known` is the customer and the project as the list's row has them, so these reads start beside the ticket's own
 *  instead of after it; once the ticket has answered it names them itself (the same addresses). The customer sidebar round (2026-11-01): the
 *  list asks for the customer's 20 newest tickets (every live status: the server ignores the view for one customer's list), and when the
 *  facts answer says it changed the stored customer (licences, country, language) the customer is read again, so the first open shows it;
 *  since the customer sources panel round (2026-11-04) that read is a new request (the one joined within 250 ms came back with the old rows) and
 *  the licences come from the lookup's own answer first. */
export type TicketKnown = { customerId: string; projectId: string };
/** who is on the tickets the inbox shows, read once for the list and the pane */
export type TicketPresence = Record<number, PresenceRow[]>;
export const CUSTOMER_HISTORY_LIMIT = 20;
export function useTicketSide(slug: string, ticket: Ticket | null, known: TicketKnown | null | undefined) {
  const customerId = ticket?.customer.id ?? known?.customerId ?? null, projectId = ticket?.projectId ?? known?.projectId;
  const fromSystem = useFacts(slug, customerId, projectId);
  const customer = useCustomer(slug, customerId);
  const wrote = fromSystem.data?.wrote === true;
  // the customer sources panel round (2026-11-04, finding 1): always a new read, never joined to the one that began before the lookup's write
  useEffect(() => { if (wrote) void customer.reload({ fresh: true }); }, [wrote, fromSystem.data]); // eslint-disable-line react-hooks/exhaustive-deps -- once per answer that wrote
  return {
    fromSystem, customer,
    history: useFetch<TicketPage>(customerId ? listUrl(slug, { customer: customerId, view: "all-open", limit: String(CUSTOMER_HISTORY_LIMIT) }) : null),
  };
}
